import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { ensure } from 'components/sharpr/ensure/ensure.js';

export function EmailAddressBookRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.EmailAddressBook', {
		url: '/email/address_book',
		controllerAs: 'vm',
		controller: EmailAddressBookCtrl,
		templateUrl: '/ng/hub/Email/AddressBook/EmailAddressBookCtrl.html?' + window.BUILD,
		resolve: {
			$style: () => AssetManager.css('/ng/hub/Email/AddressBook/EmailAddressBookCtrl.css'),
			roles: ApiResourcify.endpoint('/users/roles'),
			divisionUsers: ApiResourcify.endpoint('/analytics/division/users'),
			organizationUsers: ApiResourcify.endpoint('/analytics/organization/users'),
			distributionLists: ApiResourcify.endpoint('/distribution_lists'),
		},
	});
}

export class EmailAddressBookCtrl {
	constructor(Api, Cfg, modal, $timeout, roles, divisionUsers, organizationUsers, distributionLists, $window) {
		'ngInject';
		this.Api = Api;
		this.Cfg = Cfg;
		this.modal = modal;
		this.$window = $window;
		this.roles = ensure.array(roles);
		this.divisionUsers = ensure.array(divisionUsers);
		this.organizationUsers = ensure.array(organizationUsers);
		this.distributionLists = ensure.array(distributionLists);
		this.recipients = [];

		this.divisionIsEnabled = !!parseInt(this.Cfg.read('UI.divisions_filter'));
		this.divisionLabel = this.Cfg.read('UI.divisions_filter_label');
		this.organizationIsEnabled = !!parseInt(this.Cfg.read('UI.organizations_filter'));
		this.organizationLabel = this.Cfg.read('UI.organizations_filter_label');

		// these are called by the dataTable component, so we need to give them the proper `this`
		this.checkForSelectedUsers = this.checkForSelectedUsers.bind(this);
		this.clickedUserCheckbox = this.clickedUserCheckbox.bind(this);

		// `recipients` is sent in from angular-backed pages in `locals`. Those aren't available until after
		$timeout(() => this.initialize());
	}

	initialize() {
		this.dataTableLimit = 25;
		// Functions that update something outside of the modal. Functions passed in from `locals` or default to jQuery suggester.
		this.updates = {
			updateDistList: this.updateDistList || this.$window.parent.rintel.editDistListName,

			removeRole: this.removeRecipientItem || this.$window.parent.rintel.removeRole,
			addRole: this.addRecipientItem || this.$window.parent.rintel.addRole,

			removeDivision: this.removeRecipientItem || this.$window.parent.rintel.removeDivision,
			addDivision: this.addRecipientItem || this.$window.parent.rintel.addDivision,

			removeOrganization: this.removeRecipientItem || this.$window.parent.rintel.removeOrganization,
			addOrganization: this.addRecipientItem || this.$window.parent.rintel.addOrganization,

			removeDistList: this.removeRecipientItem || this.$window.parent.rintel.removeDistList,
			addDistList: this.addRecipientItem || this.$window.parent.rintel.addDistList,

			removeEmail: this.removeRecipientItem || this.$window.parent.rintel.removeRecipient,
			addEmail: this.addRecipientItem || this.$window.parent.rintel.addRecipient,
		};

		if (this.$window.parent.suggester) {
			this.recipients = this.$window.parent.suggester.getValues();
		}

		this.checkForSelectedRolesAndLists();
	}

	gatherRolesAndDistLists(recipients) {
		var selectedRoles = [];
		var selectedDivisions = [];
		var selectedOrganizations = [];
		var selectedLists = [];

		recipients.forEach(recipient => {
			if (recipient.match(/role:/)) {
				// suggesterItem.value is like role:intel leader
				selectedRoles.push(recipient.replace('role:', ''));
			} else if (recipient.match(/division:/)) {
				selectedDivisions.push(recipient.replace('division:', ''));
			} else if (recipient.match(/organization:/)) {
				selectedOrganizations.push(recipient.replace('organization:', ''));
			} else if (recipient.match(/gid:/)) {
				// suggesterItem.value is like gid:135
				selectedLists.push(recipient.replace('gid:', ''));
			}
		});

		return { selectedRoles, selectedDivisions, selectedOrganizations, selectedLists };
	}

	/*
	 * Iterates through items in this.recipients. Makes sure these roles and lists
	 * are checked when the address book opens.
	 */
	checkForSelectedRolesAndLists() {
		if (!this.recipients) {
			return;
		}

		const { selectedRoles, selectedDivisions, selectedOrganizations, selectedLists } = this.gatherRolesAndDistLists(
			this.recipients
		);

		this.roles.forEach(role => {
			if (selectedRoles.indexOf(role.role) > -1) {
				role.checked = true;
			}
		});

		this.divisionUsers.forEach(user => {
			if (selectedDivisions.indexOf(user.label) > -1) {
				user.checked = true;
			}
		});

		this.organizationUsers.forEach(user => {
			if (selectedOrganizations.indexOf(user.label) > -1) {
				user.checked = true;
			}
		});

		this.distributionLists.forEach(list => {
			if (selectedLists.indexOf(list.id.toString()) > -1) {
				list.checked = true;
			}
		});
	}

	/*
	 * Iterates through items in `this.recipients` to find email addresses that
	 * have already been selected and checks them in the address book.
	 * Similar to checkForSelectedRolesAndLists, but this is called by the
	 * dataTable component with different arguments. It is called each time
	 * the dataTable hits the API and returns results. See dataTable's
	 * `transformer` attribute.
	 */
	checkForSelectedUsers(data, total) {
		var selectedEmails = [];
		if (this.recipients) {
			selectedEmails = this.recipients;
		}
		data.data.forEach(user => {
			if (selectedEmails.indexOf(user.email) > -1) {
				user.checked = true;
			}
		});

		return data;
	}

	clickedRoleCheckbox(roleObj) {
		roleObj.checked = !roleObj.checked;

		const method = roleObj.checked ? 'addRole' : 'removeRole';
		const roleItem = `role:${roleObj.role}`;

		this.updates[method](roleObj, roleItem);
	}

	clickedDivisionCheckbox(divisionObj) {
		divisionObj.checked = divisionObj.checked ? !divisionObj.checked : true;

		const method = divisionObj.checked ? 'addDivision' : 'removeDivision';
		const divisionItem = `division:${divisionObj.label}`;

		this.updates[method](divisionObj, divisionItem);
	}

	clickedOrganizationCheckbox(organizationObj) {
		organizationObj.checked = organizationObj.checked ? !organizationObj.checked : true;

		const method = organizationObj.checked ? 'addOrganization' : 'removeOrganization';
		const organizationItem = `organization:${organizationObj.label}`;

		this.updates[method](organizationObj, organizationItem);
	}

	clickedDistributionListCheckbox(listObj) {
		listObj.checked = !listObj.checked;

		const method = listObj.checked ? 'addDistList' : 'removeDistList';
		const listItem = `gid:${listObj.id}`;

		this.updates[method](listObj, listItem);
	}

	clickedUserCheckbox(userObj) {
		const method = userObj.checked ? 'addEmail' : 'removeEmail';

		this.updates[method](userObj, userObj.email);
	}

	newDistList() {
		this.modal.prompt({ text: 'Distribution List Name:' }).then(name => {
			this.Api.create('/distribution_lists', { name }).then(id => {
				const newList = {
					id,
					name,
					is_active: true,
					num_members: 0,
					checked: true,
				};
				this.distributionLists.push(newList);
				const updateNewList = () => {
					this.Api.get(`/distribution_lists/${id}`).then(list => {
						newList.name = list.name;
						newList.num_members = list.num_members;
						this.updates.updateDistList(id, newList.name, newList.num_members);
					});
					this.clickedDistributionListCheckbox(newList);
				};
				this.modal
					.state('hub.loggedin.EmailDistListEdit', {
						params: { id },
						heading: 'Edit Distribution List',
						width: 900,
						height: 'full',
					})
					.then(updateNewList, updateNewList);
			});
		});
	}

	editDistList(list) {
		const updateList = () => {
			this.Api.get(`/distribution_lists/${list.id}`).then(editedList => {
				list.name = editedList.name;
				list.num_members = editedList.num_members;
				this.updates.updateDistList(list.id, editedList.name, editedList.num_members);
			});
		};
		this.modal
			.state('hub.loggedin.EmailDistListEdit', {
				params: { id: list.id },
				heading: 'Edit Distribution List',
				width: 900,
				height: 'full',
			})
			.then(updateList, updateList);
	}
}
